import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Download, Search, RefreshCw, Package } from 'lucide-react';
import { getAgentOrders, type AgentDetailRequest, type AgentStage } from '@/services/reportsService';
import { downloadCSV, type CsvColumn } from '@/utils/csv';
import { toastMessage } from '@/utils/toast-message';

interface OrderRow {
  order_id: number;
  order_name: string | null;
  order_number: string | null;
  order_date: string | null;
  total_price: number;
  financial_status: string | null;
  latest_delivery_status: number | null;
  latest_delivery_status_desc: string | null;
  customer_name: string | null;
  phone: string | null;
  email: string | null;
  lead_id_fk: number | null;
  units: number;
  agent_name: string | null;
  stage: AgentStage;
  credit_basis: 'agent_tag' | 'discount_code' | null;
  credit_ref: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  request: AgentDetailRequest;
}

const PAYMENT_LABEL: Record<string, string> = {
  pending: 'COD',
  partially_paid: 'Partial',
  paid: 'Prepaid',
  voided: 'Voided',
  refunded: 'Refunded',
};

// Same stages as the dashboard cards (reports.py _DASH_STAGE_SQL), so a drill-down
// always matches the number that was clicked.
export const STAGE_LABEL: Record<AgentStage, string> = {
  delivered: 'Delivered',
  in_transit: 'In Transit',
  awaiting_pickup: 'Awaiting Pickup',
  returned: 'Returned (RTO)',
  cancelled: 'Cancelled',
};
const deliveryLabel = (row: OrderRow): string => STAGE_LABEL[row.stage] || row.latest_delivery_status_desc || '—';
const creditLabel = (row: OrderRow): string =>
  row.credit_basis === 'agent_tag' ? `Tag: ${row.credit_ref || ''}`
  : row.credit_basis === 'discount_code' ? `Code: ${row.credit_ref || ''}` : '—';

const fmtINR = (v: number) => `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (s: string | null) => (s ? s.split('T')[0] : '—');

const AgentOrdersModal = ({ isOpen, onClose, title, request }: Props) => {
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAgentOrders(request);
      const r = res as { success?: boolean; data?: OrderRow[] } | null;
      if (r?.success) setRows((r.data as OrderRow[]) || []);
      else toastMessage('Failed to load orders', 'error');
    } catch {
      toastMessage('Error loading orders', 'error');
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      fetchData();
    }
  }, [isOpen, fetchData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.order_name || r.order_number || '').toLowerCase().includes(q) ||
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.phone || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.agent_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totalRevenue = useMemo(
    () => filtered.reduce((a, r) => a + (r.total_price || 0), 0),
    [filtered]
  );

  const handleExport = () => {
    if (filtered.length === 0) {
      toastMessage('Nothing to export', 'error');
      return;
    }
    const cols: CsvColumn<OrderRow>[] = [
      { header: 'Order', accessor: r => r.order_name || r.order_number || '' },
      { header: 'Order Date', accessor: r => fmtDate(r.order_date) },
      { header: 'Agent', accessor: r => r.agent_name || '' },
      { header: 'Customer', accessor: r => r.customer_name || '' },
      { header: 'Phone', accessor: r => r.phone || '' },
      { header: 'Email', accessor: r => r.email || '' },
      { header: 'Units', accessor: 'units' },
      { header: 'Total', accessor: r => r.total_price ?? 0 },
      { header: 'Payment', accessor: r => PAYMENT_LABEL[r.financial_status || ''] || r.financial_status || '' },
      { header: 'Stage', accessor: r => deliveryLabel(r) },
      { header: 'Courier Status', accessor: r => r.latest_delivery_status_desc || '' },
      { header: 'Credited By', accessor: r => creditLabel(r) },
    ];
    const stamp = `${request.from_date}_to_${request.to_date}`;
    downloadCSV(`agent_orders_${stamp}`, cols, filtered);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-indigo-500" />
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {loading ? 'Loading…' : `${filtered.length} of ${rows.length} orders · ${fmtINR(totalRevenue)}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search order, customer, agent…"
              className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg pl-8 pr-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
            />
          </div>
          <button
            onClick={handleExport}
            disabled={loading || filtered.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <RefreshCw className="h-8 w-8 mb-3 animate-spin opacity-50" />
              <p className="text-sm">Loading orders…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">No orders found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/80 backdrop-blur">
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-2.5 font-semibold">Order</th>
                  <th className="px-4 py-2.5 font-semibold">Date</th>
                  <th className="px-4 py-2.5 font-semibold">Agent</th>
                  <th className="px-4 py-2.5 font-semibold">Customer</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Units</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Total</th>
                  <th className="px-4 py-2.5 font-semibold">Payment</th>
                  <th className="px-4 py-2.5 font-semibold">Delivery</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.map(r => (
                  <tr key={r.order_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-700 dark:text-gray-300">{r.order_name || r.order_number || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400">{fmtDate(r.order_date)}</td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300" title={creditLabel(r)}>{r.agent_name || "—"}</td>
                    <td className="px-4 py-2.5">
                      <div className="text-gray-900 dark:text-white">{r.customer_name || '—'}</div>
                      <div className="text-xs text-gray-400">{r.phone || ''}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">{r.units}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-900 dark:text-white">{fmtINR(r.total_price || 0)}</td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{PAYMENT_LABEL[r.financial_status || ''] || r.financial_status || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{deliveryLabel(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentOrdersModal;
